import { format, parse } from 'date-fns';
import React, {
  ChangeEvent,
  SVGProps,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellDelete } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { isValidTime } from 'shared/utils/is-valid-time';
import { parseDateTime } from 'shared/utils/parse-date-time';
import { processStringToHoursAndMinutes } from 'shared/utils/process-string-to-hours-and-minutes';

const initialOptions = [
  9 * 60 * 60 * 1000, // 09:00
  12 * 60 * 60 * 1000, // 12:00
  15 * 60 * 60 * 1000, // 15:00
  18 * 60 * 60 * 1000, // 18:00
  21 * 60 * 60 * 1000, // 21:00
];

export type TimePickerMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'onOutside'
> & {
  timeFormat: TimeFormatOptions;
  onChange: (value?: number) => void;
  onCancel: () => void;
  value?: number | null;
  options?: number[];
  placeholder?: string;
  optionIcon?: React.FC<SVGProps<SVGSVGElement>>;
  hasCustomInput?: boolean;
  canClear?: boolean;
  clearLabel?: string;
};

export const TimePickerMenu: React.FC<TimePickerMenuProps> = ({
  timeFormat,
  onChange,
  onCancel,
  value,
  options = initialOptions,
  placeholder,
  optionIcon = Bell,
  hasCustomInput,
  canClear,
  clearLabel,
  ...rest
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputError, setInputError] = useState(false);
  const [inputValue, setInputValue] = useState<string>(
    value ? parseDateTime(value, timeFormat) : '',
  );

  const onClear = () => {
    onChange();
  };

  const submit = () => {
    if (!inputRef.current && hasCustomInput) {
      return;
    }

    const inputVal = inputRef.current?.value;

    if (inputVal && isValidTime(inputVal, timeFormat)) {
      const date = parse(inputVal, timeFormat, new Date());
      onChange((date.getHours() * 60 + date.getMinutes()) * 60 * 1000);
      return;
    }

    onChange();
  };

  const onOutside = () => {
    const value = inputRef.current?.value;

    if (!value || isValidTime(value, timeFormat)) {
      submit();
      return;
    }

    onCancel();
  };

  const handleInputChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setInputError(false);
    if (!target.value.length) {
      setInputValue('');
      return;
    }

    // Process textual input based on the selected format
    const processedValue = processStringToHoursAndMinutes(
      target.value,
      timeFormat,
    );

    // Update the input
    setInputValue(processedValue);
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useKeysDown(
    ['Enter'],
    (e) => {
      if (inputRef.current && document.activeElement === inputRef.current) {
        e.preventDefault();

        const value = inputRef.current.value;
        if (!value || isValidTime(value, timeFormat)) {
          submit();
          return;
        }

        setInputError(true);
      }
    },
    { otherKeysInCombinationAllowed: true },
  );

  const _placeHolder = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }

    if (!options.length) {
      return timeFormat;
    }

    const dateObj = new Date();
    dateObj.setHours(0, 0, 0, options[0]);
    return format(dateObj, timeFormat);
  }, [options, placeholder, timeFormat]);

  return (
    <PopupMenu onOutside={onOutside} {...rest}>
      {!!hasCustomInput && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuInput
              ref={inputRef}
              placeholder={_placeHolder}
              iconColor={inputError ? Colors.Error : undefined}
              startIcon={optionIcon}
              textColor={inputError ? Colors.Error : undefined}
              value={inputValue}
              onChange={handleInputChange}
            />
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      <PopupMenuList hasBottomBorder={!!value && canClear}>
        {options.map((option) => {
          const onItem = () => onChange(option);

          const dateObj = new Date();
          dateObj.setHours(0, 0, 0, option);

          return (
            <PopupMenuListItem key={option}>
              <PopupMenuButton
                start={<Icon icon={optionIcon} />}
                onClick={onItem}
                tabIndex={0}
                type="button"
              >
                {format(dateObj, timeFormat)}
              </PopupMenuButton>
            </PopupMenuListItem>
          );
        })}
      </PopupMenuList>

      {canClear && !!value && (
        <PopupMenuList>
          <PopupMenuListItem>
            <PopupMenuButton
              start={<Icon icon={BellDelete} />}
              startColor={Colors.Error}
              textColor={Colors.Error}
              onClick={onClear}
              tabIndex={0}
            >
              {clearLabel ?? t('task.reminder.options.no-reminder.label')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}
    </PopupMenu>
  );
};
